import {
  ApolloClient,
  DataProxy,
  FetchResult,
  useApolloClient,
} from '@apollo/client';
import { Delete, FileCopy, History, RemoveRedEye } from '@mui/icons-material';
import RssFeedIcon from '@mui/icons-material/RssFeed';
import {
  EditAppBar,
  LockStatus,
  NotificationTypeEnum,
  useConcurrentialAccess,
  useDialog,
  useNotification,
  useOnMount,
} from '@prismamedia/one-components';
import { useEditContext } from '@prismamedia/one-crud-react';
import { Role } from '@prismamedia/one-user';
import {
  Campaign,
  SectionFragment,
  UpdateSectionMutationVariables,
  UpsertCampaignMutation,
  UpsertCampaignMutationVariables,
  UpsertCampaignMutation_upsertCampaign_Campaign,
} from '__generated__/queries-newsletter';
import { deleteCampaign } from 'apollo/api';
import { useUpdateCampaignLockerId } from 'apollo/mutations/updateCampaign.newsletter.graphql';
import { UPDATE_SECTION_MUTATION } from 'apollo/mutations/updateSection.newsletter.graphql';
import { UPSERT_CAMPAIGN_MUTATION } from 'apollo/mutations/upsertCampaign.newsletter.graphql';
import { ConfirmDialog } from 'components/ConfirmDialog';
import { isPast } from 'date-fns';
import moment from 'moment';
import { clone, differenceWith, isEmpty } from 'ramda';
import { FC, useCallback, useMemo, useState } from 'react';
import { generatePath, useHistory, useRouteMatch } from 'react-router-dom';
import { paths, replaceParams } from 'routing';
import { auth } from 'utils/auth';
import { CampaignHistory } from '../CampaignHistory';
import {
  campaignMutationVariables,
  isCampaignValid,
} from '../SaveButton/utils';
import {
  CONFIRM_CAMPAIGN_CREATION_TEXT,
  CONFIRM_LABEL,
  DECLINE_LABEL,
  DUPPLICATED_CAMPAIGN_CONFIRMATION_TEXT,
  REMOVED_CAMPAIGN_CONFIRMATION_TEXT,
  REMOVED_CAMPAIGN_ERROR_TEXT,
  RSS_XML_NEWSLETTER_ID_ERROR_TEXT,
  SAVED_CAMPAIGN_CONFIRMATION_TEXT,
  SAVED_CAMPAIGN_DATE_ERROR_TEXT,
  SAVED_CAMPAIGN_ERROR_TEXT,
  SAVED_CAMPAIGN_LOCK_ERROR_TEXT,
} from './constants';
import {
  computeCampaignRssUrl,
  computePreviewUrl,
  getUnlockLink,
  transformItem,
} from './utils';

let idsOfCreatedBlocksAndSections: Set<string> = new Set([]);

interface AppBarProps {
  brand?: string | null;
  onSave: (
    item: UpsertCampaignMutation_upsertCampaign_Campaign,
    shouldUpdate: boolean,
  ) => Promise<void | null>;
  isCreate: boolean;
  id: string;
  item: Campaign;
}

export const AppBar: FC<AppBarProps> = ({
  brand,
  isCreate,
  onSave,
  id,
  item,
}) => {
  const history = useHistory();
  const context = useEditContext();
  const { pushNotification } = useNotification();
  const [saveLoading, setSaveLoading] = useState(false);
  const { openDialog, closeDialog } = useDialog();
  const client = useApolloClient() as ApolloClient<DataProxy>;
  const currentCampaign = context.item as Campaign;
  const { newsletter } = currentCampaign;
  const match = useRouteMatch<{ id: string }>();
  const updateLockerId = useUpdateCampaignLockerId();
  const isAdmin = useMemo(
    () =>
      auth.user?.isSuperAdmin() ||
      auth.user?.isAdmin() ||
      auth.user?.hasRole(Role.WebAdmin),
    [],
  );
  const [lockStatus, setLockStatus] = useState<LockStatus | undefined | null>(
    undefined,
  );

  const onLock = useCallback(
    async (isInitialLock) => {
      try {
        const { data } = await updateLockerId(
          item.id,
          auth?.user?.id || null,
          !isInitialLock,
        );
        setLockStatus(data?.updateCampaign);
      } catch (e) {
        if (
          e instanceof Error &&
          e.message !== 'ER_ALREADY_SENT: Campaign already sent'
        ) {
          throw e;
        }
      }
    },
    [item.id, updateLockerId],
  );

  useConcurrentialAccess({
    lockStatus: isCreate ? null : currentCampaign,
    unlockTimeout: 600000,
    currentUser: auth.user,
    onLock,
    unlockLink: getUnlockLink(item.id),
    onNavigateBack: () => history.push(paths.CAMPAIGN_LIST),
    disableLockForV0: true,
  });

  const addIds = (content: Record<string, any>) => {
    const mapper = (elem: Record<string, any>) => {
      if (
        (elem.isCreate && !idsOfCreatedBlocksAndSections.has(elem.id)) ||
        (elem.isCreate === undefined &&
          !idsOfCreatedBlocksAndSections.has(elem.id))
      ) {
        idsOfCreatedBlocksAndSections.add(elem.id);
      }
      if (elem.blocks) {
        elem.blocks.forEach(mapper);
      }
    };
    content.sections.forEach(mapper);
  };

  const beforeSave = (campaignItem: any) => {
    const { validOrError, errorReason } = isCampaignValid(campaignItem);

    if (validOrError && errorReason) {
      pushNotification({
        message: errorReason,
        type: NotificationTypeEnum.error,
      });
    }

    if (!validOrError && errorReason) {
      pushNotification({
        message: errorReason,
        type: NotificationTypeEnum.error,
      });
      return null;
    }

    if (isCreate) {
      idsOfCreatedBlocksAndSections = new Set([]);
    }

    const cleanedItem = transformItem(
      campaignItem,
      idsOfCreatedBlocksAndSections,
    );

    addIds(campaignItem);

    return cleanedItem;
  };

  const saveItem = async () => {
    if (isEmpty(context.item)) {
      return;
    }

    const itemToSave = clone(context.item);
    const contextItem = beforeSave(itemToSave as Campaign);
    if (!contextItem) {
      return;
    }

    const variables: UpsertCampaignMutationVariables = campaignMutationVariables(
      contextItem,
      item,
      id,
      isCreate,
    );
    if (!isCreate) {
      /**
       * In an update we need to check if a section is deleted in order to delete
       * before its related blocks.
       */
      const deletedSections: SectionFragment[] = differenceWith(
        (x: SectionFragment, y: SectionFragment) => x.id === y.id,
        item.sections,
        contextItem.sections,
      );
      if (!isEmpty(deletedSections)) {
        const promises = deletedSections.map((deletedSection) => {
          const updateVariables: UpdateSectionMutationVariables = {
            data: {
              blocks: {
                delete: deletedSection.blocks.map((block) => ({
                  id: block.id,
                })),
              },
            },
            where: {
              id: deletedSection.id,
            },
          };
          return client.mutate({
            mutation: UPDATE_SECTION_MUTATION,
            variables: updateVariables,
          });
        });
        await Promise.all(promises);
      }
    }

    setSaveLoading(true);
    try {
      const res:
        | FetchResult<UpsertCampaignMutation>
        | { data: null; error: string } = await client.mutate<
        UpsertCampaignMutation,
        UpsertCampaignMutationVariables
      >({
        mutation: UPSERT_CAMPAIGN_MUTATION,
        variables,
      });

      if (res?.data?.upsertCampaign?.id) {
        onSave(res.data.upsertCampaign, false);

        pushNotification({
          message: SAVED_CAMPAIGN_CONFIRMATION_TEXT,
          type: NotificationTypeEnum.success,
        });
      } else {
        pushNotification({
          message: SAVED_CAMPAIGN_ERROR_TEXT,
          type: NotificationTypeEnum.error,
        });
      }

      if (isCreate && res?.data?.upsertCampaign?.id) {
        history.replace(
          replaceParams(paths.CAMPAIGN_EDIT, {
            id: res.data.upsertCampaign.id,
          }),
        );
      }
    } catch (e: any) {
      console.error(e);
      let message = SAVED_CAMPAIGN_ERROR_TEXT;

      if (e.message.match(/ER_DUP_ENTRY.*unq_newsletterIntId_availableAt/)) {
        message = SAVED_CAMPAIGN_DATE_ERROR_TEXT;
      } else if (e.message.match(/ER_ALREADY_SENT/)) {
        message = SAVED_CAMPAIGN_LOCK_ERROR_TEXT;
      }
      pushNotification({
        message,
        type: NotificationTypeEnum.error,
      });
    }
    setSaveLoading(false);
  };

  const deleteItem = async () => {
    if (isEmpty(context.item)) {
      return;
    }

    const campaignItem: Campaign = context.item as Campaign;
    const res: FetchResult = await deleteCampaign(campaignItem, client);

    if (res?.data?.deleteCampaign?.id) {
      pushNotification({
        message: REMOVED_CAMPAIGN_CONFIRMATION_TEXT,
        type: NotificationTypeEnum.success,
      });
    } else {
      pushNotification({
        message: REMOVED_CAMPAIGN_ERROR_TEXT,
        type: NotificationTypeEnum.error,
      });
    }

    if (res?.data?.deleteCampaign?.id) {
      history.push(paths.CAMPAIGN_LIST);
    }
  };

  useOnMount(() => {
    addIds(context.item);
  });

  return (
    <EditAppBar
      onNavigateBack={() => {
        updateLockerId(item.id, null).catch(() => {});
        history.push(paths.CAMPAIGN_LIST);
      }}
      title={`${context.item.subject || ''} - ${brand}`}
      subTitle={moment(context.item.updatedAt).format('D MMM YYYY, HH[h]mm')}
      save={{
        onClick: saveItem,
        loading: saveLoading,
      }}
      actions={[
        ...(isAdmin
          ? [
              {
                id: 'see-rss',
                label: 'Flux XML',
                icon: <RssFeedIcon />,
                onClick: () => {
                  if (!currentCampaign.newsletter?._id) {
                    pushNotification({
                      message: RSS_XML_NEWSLETTER_ID_ERROR_TEXT,
                      type: NotificationTypeEnum.error,
                    });
                    return;
                  }

                  window.open(
                    config?.API_URL &&
                      computeCampaignRssUrl(
                        config.API_URL,
                        currentCampaign.newsletter._id,
                        currentCampaign._id,
                      ),
                  );
                },
              },
            ]
          : []),
        {
          id: 'see-front',
          label: 'Visualiser',
          icon: <RemoveRedEye />,
          onClick: () =>
            newsletter?.linkUrl &&
            window.open(
              computePreviewUrl(newsletter.linkUrl, currentCampaign._id),
            ),
        },
        {
          id: 'history',
          label: 'Historique',
          icon: <History />,
          onClick: () =>
            openDialog(<CampaignHistory histories={item.campaignHistories} />),
        },
        ...(!isCreate
          ? [
              {
                id: 'duplicate',
                label: 'Dupliquer',
                icon: <FileCopy />,
                onClick: () => {
                  history.push(
                    `${generatePath(paths.DUPLICATE_CAMPAIGN, {
                      id: match.params.id,
                    })}${location.search}`,
                  );
                  pushNotification({
                    message: DUPPLICATED_CAMPAIGN_CONFIRMATION_TEXT,
                    type: NotificationTypeEnum.success,
                  });
                },
              },
            ]
          : []),
        ...(!isCreate
          ? [
              {
                id: 'delete',
                label: 'Supprimer',
                icon: <Delete />,
                disabled:
                  !!item.availableAt && isPast(new Date(item.availableAt)),
                onClick: () =>
                  openDialog(
                    <ConfirmDialog
                      title={CONFIRM_CAMPAIGN_CREATION_TEXT}
                      confirmLabel={CONFIRM_LABEL}
                      cancelLabel={DECLINE_LABEL}
                      onConfirm={deleteItem}
                      closeDialog={closeDialog}
                    />,
                  ),
              },
            ]
          : []),
      ]}
      concurrentialAccess={{
        lockStatus: lockStatus || currentCampaign,
        disableLockForV0: true,
      }}
    />
  );
};
